import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';

import { studentApi } from '@/api';
import { resolveFileUrl } from '@/api/client';
import {
  Alert,
  Badge,
  Button,
  Card,
  EmptyState,
  ErrorState,
  Icon,
  LinkButton,
  Select,
  Skeleton,
  useToast,
} from '@/components/ui';
import { Tabs } from '@/components/shell';
import { bandOf, groupChecks, tabFrom, topFixes } from '@/features/resume/cvScore';
import { severityOf } from '@/features/resume/gaps';
import { ResumeGapSection } from '@/features/resume/ResumeGapSection';
import { PLAN_PATH } from '@/features/studentBilling/plans';
import { errorMessage } from '@/hooks/useApiError';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';
import { timeAgo } from '@/utils/format';

/** "What holds it back" severities, as the AI review writes them. */
const ISSUE_SEVERITY = {
  high: { label: 'High', tone: 'danger' },
  medium: { label: 'Medium', tone: 'warning' },
  low: { label: 'Low', tone: 'neutral' },
};

const SECTION_LABELS = {
  summary: 'Summary',
  experience: 'Experience',
  education: 'Education',
  skills: 'Skills',
  projects: 'Projects',
  certifications: 'Certifications',
  formatting: 'Formatting',
  contact: 'Contact',
  general: 'General',
};

/**
 * One CV, scored and improved -- laid out the way resume-score tools are: a
 * summary band (the score and verdict, the file, the three fixes worth most),
 * then tabs for the detail.
 *
 * Three independent loads, so a slow or failed one never hides the others:
 * the counted score (`/score`, instant, free), the written feedback
 * (`/review`, AI when available, may take 20 seconds) and the gap analysis
 * (rules, free). The page says which parts are AI-written.
 */
export function StudentCvFeedbackPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const toast = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = tabFrom(searchParams.get('tab'));
  const [pendingAnchor, setPendingAnchor] = useState(null);
  const reviewKey = ['resume-review', id];

  const score = useQuery({
    queryKey: ['resume-score', id],
    queryFn: () => studentApi.getResumeScore(id),
    retry: false,
  });
  const review = useQuery({
    queryKey: reviewKey,
    queryFn: () => studentApi.reviewResume(id),
    // The server caches for a week; a revisit in the same session need not ask.
    staleTime: 30 * 60 * 1000,
    retry: false,
  });
  // Shared with the gap section below (same key), for the tab count and the
  // top fixes.
  const gaps = useQuery({
    queryKey: ['resume-gaps', id],
    queryFn: () => studentApi.getResumeGapAnalysis(id),
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
  const resumes = useQuery({ queryKey: ['student-resumes'], queryFn: studentApi.listResumes });

  const refresh = useMutation({
    mutationFn: () => studentApi.reviewResume(id, { refresh: true }),
    onSuccess: (fresh) => {
      queryClient.setQueryData(reviewKey, fresh);
      toast.success('New feedback ready');
    },
    onError: (refreshError) =>
      toast.error(errorMessage(refreshError, 'Could not get new feedback')),
  });

  // A click on a top fix switches tab, then scrolls once the panel exists.
  useEffect(() => {
    if (!pendingAnchor) return undefined;
    const timer = window.setTimeout(() => {
      document
        .getElementById(pendingAnchor)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      setPendingAnchor(null);
    }, 60);
    return () => window.clearTimeout(timer);
  }, [pendingAnchor, tab]);

  function selectTab(key) {
    setSearchParams(key === 'overview' ? {} : { tab: key }, { replace: true });
  }

  function goToFix(fix) {
    selectTab(fix.tab);
    setPendingAnchor(fix.anchor);
  }

  async function download() {
    try {
      // URLs are signed and short-lived, so fetch a fresh one per click.
      const { download_url } = await studentApi.getResumeDownloadUrl(id);
      window.open(resolveFileUrl(download_url), '_blank', 'noopener');
    } catch (downloadError) {
      toast.error(errorMessage(downloadError, 'Could not open the file'));
    }
  }

  const backLink = (
    <Link
      to={paths.student.cv}
      className="text-ink-muted hover:text-ink mb-3 inline-flex items-center gap-1 text-sm transition"
    >
      <Icon name="chevron" size={16} className="rotate-180" />
      Back to my CVs
    </Link>
  );

  if (score.isError) {
    return (
      <div>
        {backLink}
        <ErrorState
          title="We could not score this CV"
          message={errorMessage(score.error)}
          onRetry={() => void score.refetch()}
        />
      </div>
    );
  }

  const checks = score.data?.checks ?? [];
  const passed = checks.filter((check) => check.passed).length;
  const suggestions = review.data?.suggestions ?? [];
  const fixes = score.data
    ? topFixes({
        checks,
        fixes: score.data.fixes,
        gaps: gaps.data,
        suggestions,
      })
    : [];

  const tabs = [
    { key: 'overview', label: 'Overview', icon: 'overview' },
    {
      key: 'improvements',
      label: 'Improvements',
      icon: 'sparkles',
      count: review.data ? suggestions.length : null,
    },
    {
      key: 'checklist',
      label: 'Checklist',
      icon: 'check',
      count: score.data ? `${passed}/${checks.length}` : null,
    },
    {
      key: 'gaps',
      label: 'Gap analysis',
      icon: 'target',
      count: gaps.data ? gaps.data.summary.total : null,
    },
    { key: 'match', label: 'Match to a role', icon: 'search' },
  ];

  return (
    <div className="min-w-0">
      {backLink}

      {score.isPending ? (
        <BandSkeleton />
      ) : (
        <SummaryBand
          score={score.data}
          review={review}
          resumeId={id}
          resumes={resumes.data ?? []}
          fixes={fixes}
          onFix={goToFix}
          onRefresh={() => refresh.mutate()}
          isRefreshing={refresh.isPending}
          onDownload={() => void download()}
          onSwitch={(next) =>
            navigate({
              pathname: paths.student.cvFeedback(next),
              search: tab === 'overview' ? '' : `?tab=${tab}`,
            })
          }
        />
      )}

      <FeedbackNotice review={review.data} />

      <div className="lg:bg-background/95 mt-4 lg:sticky lg:top-[55px] lg:z-20 lg:backdrop-blur">
        <Tabs tabs={tabs} value={tab} onChange={selectTab} label="CV score sections" />
      </div>

      <div className="mt-4" role="tabpanel" aria-label={tabs.find((t) => t.key === tab)?.label}>
        {tab === 'overview' && <OverviewTab review={review} />}
        {tab === 'improvements' && <ImprovementsTab review={review} />}
        {tab === 'checklist' &&
          (score.isPending ? (
            <Skeleton className="h-64 w-full" />
          ) : (
            <ChecklistTab checks={checks} fixes={score.data.fixes} gapItems={gaps.data} />
          ))}
        {tab === 'gaps' && <ResumeGapSection resumeId={id} />}
        {tab === 'match' && <MatchTab source={review.data?.source} />}
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Summary band
// --------------------------------------------------------------------------- //
function SummaryBand({
  score,
  review,
  resumeId,
  resumes,
  fixes,
  onFix,
  onRefresh,
  isRefreshing,
  onDownload,
  onSwitch,
}) {
  const band = bandOf(score.score, score.grade);
  const analysed = review.data?.generated_at;

  return (
    <Card className="p-0">
      <div className="grid lg:grid-cols-[minmax(0,23rem)_minmax(0,1fr)]">
        {/* Left: the score, then the file and what to do with it. */}
        <div className="flex min-w-0 flex-col gap-4 p-4 sm:p-5">
          <div className="flex items-center gap-4">
            <ScoreGauge score={score.score} ring={band.ring} />
            <div className="min-w-0">
              <Badge tone={band.tone} dot>
                {band.key}
              </Badge>
              <p className="text-ink mt-1.5 text-sm font-medium">{band.verdict}</p>
              <p className="text-ink-muted mt-0.5 text-xs">
                Counted from your CV: the same file always gets the same score.
              </p>
            </div>
          </div>

          <div className="border-line min-w-0 space-y-2.5 border-t pt-3">
            <div className="flex min-w-0 items-center gap-2">
              <Icon name="fileText" size={15} className="text-ink-muted shrink-0" />
              <p
                className="text-ink min-w-0 truncate text-sm font-semibold"
                title={score.file_name}
              >
                {score.file_name}
              </p>
              <span className="text-ink-muted shrink-0 text-xs">
                ·{' '}
                {review.isPending
                  ? 'Writing feedback…'
                  : analysed
                    ? `Feedback ${timeAgo(analysed)}`
                    : 'Scored just now'}
              </span>
            </div>
            {resumes.length > 1 && (
              <Select
                label="Switch CV"
                hideLabel
                size="sm"
                value={resumeId}
                onChange={(event) => onSwitch(event.target.value)}
              >
                {resumes.map((row) => (
                  <option key={row.id} value={row.id}>
                    {row.file_name}
                    {row.is_primary ? ' (primary)' : ''}
                  </option>
                ))}
              </Select>
            )}
            <div className="flex flex-wrap gap-2">
              <Button
                size="sm"
                variant="tintBrand"
                onClick={onRefresh}
                isLoading={isRefreshing}
                disabled={review.isPending}
              >
                <Icon name="repeat" size={14} />
                Get new feedback
              </Button>
              <Button size="sm" variant="secondary" onClick={onDownload}>
                <Icon name="download" size={14} />
                Download CV
              </Button>
            </div>
          </div>
        </div>

        {/* Right: the fixes worth most, each a click from its detail. */}
        <div className="border-line bg-surface-muted/40 min-w-0 border-t p-4 sm:p-5 lg:border-t-0 lg:border-l">
          <h2 className="text-ink-muted text-[11px] font-semibold tracking-wide uppercase">
            {fixes.length === 0
              ? 'Top fixes'
              : `Top ${fixes.length === 1 ? 'fix' : `${fixes.length} fixes`}`}
          </h2>
          {fixes.length === 0 ? (
            <div className="bg-success-50/60 ring-success-100 rounded-input mt-2 flex items-start gap-3 px-3.5 py-3 ring-1 ring-inset">
              <Icon name="check" size={15} className="text-success-700 mt-0.5 shrink-0" />
              <p className="text-success-800 text-sm">
                Nothing urgent to fix. Tailor the CV to each role you apply for.
              </p>
            </div>
          ) : (
            <ol className="mt-2 space-y-2">
              {fixes.map((fix, index) => {
                const severity = severityOf(fix.severity);
                return (
                  <li key={fix.id}>
                    <button
                      type="button"
                      onClick={() => onFix(fix)}
                      className="border-line bg-surface hover:border-brand-300 focus-visible:outline-brand-600 rounded-input group flex w-full items-start gap-2.5 border p-2.5 text-left transition focus-visible:outline-2"
                    >
                      <span className="bg-brand-600 grid size-5 shrink-0 place-items-center rounded-full text-[11px] font-semibold text-white">
                        {index + 1}
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-1.5">
                          <span className="text-ink text-[13px] font-semibold">{fix.title}</span>
                          <Badge tone={severity.tone}>{severity.label}</Badge>
                        </span>
                        <span className="text-ink-muted mt-0.5 line-clamp-2 block text-xs">
                          {fix.text}
                        </span>
                      </span>
                      <Icon
                        name="arrowRight"
                        size={14}
                        className="text-ink-muted group-hover:text-brand-600 mt-0.5 shrink-0"
                      />
                    </button>
                  </li>
                );
              })}
            </ol>
          )}
        </div>
      </div>
    </Card>
  );
}

function BandSkeleton() {
  return (
    <Card>
      <div className="grid gap-5 lg:grid-cols-[23rem_minmax(0,1fr)]" aria-live="polite">
        <div className="space-y-4">
          <div className="flex items-center gap-4">
            <Skeleton className="size-28 rounded-full" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-3 w-full" />
            </div>
          </div>
          <Skeleton className="h-8 w-2/3" />
        </div>
        <div className="space-y-2">
          {[0, 1, 2].map((row) => (
            <Skeleton key={row} className="h-14 w-full" />
          ))}
        </div>
      </div>
    </Card>
  );
}

/** The score as a ring: the number in the middle, the band's colour around it. */
function ScoreGauge({ score, ring }) {
  const radius = 42;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - Math.max(0, Math.min(100, score)) / 100);
  return (
    <div className="relative size-28 shrink-0">
      <svg viewBox="0 0 100 100" className="size-full -rotate-90" aria-hidden="true">
        <circle cx="50" cy="50" r={radius} className="stroke-line" strokeWidth="9" fill="none" />
        <circle
          cx="50"
          cy="50"
          r={radius}
          fill="none"
          strokeWidth="9"
          strokeLinecap="round"
          stroke="currentColor"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          className={cn('transition-all duration-700', ring)}
        />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <p className="text-ink text-3xl leading-none font-semibold tabular-nums">{score}</p>
          <p className="text-ink-muted mt-1 text-[11px]">out of 100</p>
        </div>
      </div>
      <span className="sr-only">CV score {score} out of 100</span>
    </div>
  );
}

/** Over the allowance, or written from the checklist: said calmly, once. */
function FeedbackNotice({ review }) {
  if (!review) return null;
  if (review.limit_reached) {
    return (
      <Alert tone="info" title="AI reviews used up for this month" className="mt-4">
        <p>
          {review.upgrade_hint ??
            "You've used your AI reviews this month — showing checklist feedback. Upgrade for unlimited AI reviews."}
        </p>
        <Link
          to={PLAN_PATH}
          className="text-brand-700 hover:text-brand-800 mt-1 inline-block font-medium underline underline-offset-2"
        >
          See Premium
        </Link>
      </Alert>
    );
  }
  if (review.source !== 'ai') {
    return (
      <Alert tone="info" className="mt-4">
        AI feedback is not available right now, so the written feedback comes from the checklist.
        Your score is the same either way.
      </Alert>
    );
  }
  return null;
}

// --------------------------------------------------------------------------- //
// AI parts: shared states
// --------------------------------------------------------------------------- //
function Writing() {
  return (
    <Card>
      <div className="flex items-center gap-3" aria-live="polite">
        <span className="bg-brand-50 text-brand-600 grid size-9 place-items-center rounded-xl">
          <Icon name="sparkles" size={18} className="animate-pulse" />
        </span>
        <div>
          <p className="text-ink text-sm font-semibold">Writing feedback…</p>
          <p className="text-ink-muted text-xs">
            This can take up to 20 seconds. Your score, checklist and gap analysis are ready now.
          </p>
        </div>
      </div>
      <div className="mt-4 space-y-2.5">
        <Skeleton className="h-4 w-1/3" />
        <Skeleton className="h-3.5 w-full" />
        <Skeleton className="h-3.5 w-5/6" />
      </div>
    </Card>
  );
}

function ReviewFailed({ review }) {
  return (
    <ErrorState
      title="We could not write feedback for this CV"
      message={`${errorMessage(review.error)} Your score, checklist and gap analysis still work.`}
      onRetry={() => void review.refetch()}
    />
  );
}

function SectionTitle({ icon, title, description, action }) {
  return (
    <div className="mb-3 flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
      <div className="flex min-w-0 items-start gap-2.5">
        <span
          aria-hidden="true"
          className="bg-brand-50 text-brand-700 ring-brand-100 grid size-8 shrink-0 place-items-center rounded-lg ring-1 ring-inset"
        >
          <Icon name={icon} size={15} />
        </span>
        <div className="min-w-0">
          <h2 className="text-ink text-base font-semibold">{title}</h2>
          {description && <p className="text-ink-muted mt-0.5 text-[12px]">{description}</p>}
        </div>
      </div>
      {action}
    </div>
  );
}

function SourceBadge({ source }) {
  return (
    <Badge tone={source === 'ai' ? 'brand' : 'neutral'}>
      <Icon name={source === 'ai' ? 'sparkles' : 'check'} size={12} />
      {source === 'ai' ? 'Written by AI' : 'Written from the checklist'}
    </Badge>
  );
}

// --------------------------------------------------------------------------- //
// Overview
// --------------------------------------------------------------------------- //
function OverviewTab({ review }) {
  if (review.isPending) return <Writing />;
  if (review.isError) return <ReviewFailed review={review} />;
  const data = review.data;

  return (
    <div className="space-y-4">
      <Card>
        <SectionTitle icon="info" title="In short" action={<SourceBadge source={data.source} />} />
        <p className="text-ink text-sm leading-6">{data.summary}</p>
      </Card>

      <div className="grid items-start gap-4 lg:grid-cols-2">
        <Card>
          <SectionTitle icon="star" title="What already works" />
          {data.strengths.length === 0 ? (
            <EmptyState
              compact
              title="Nothing stands out yet — the fixes above will change that."
            />
          ) : (
            <ul className="space-y-2">
              {data.strengths.map((strength, index) => (
                <li key={index} className="text-ink flex gap-2 text-sm">
                  <Icon name="check" size={15} className="text-success-600 mt-0.5 shrink-0" />
                  {strength}
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card>
          <SectionTitle icon="alert" title="What holds it back" />
          {data.issues.length === 0 ? (
            <EmptyState compact title="Nothing holding it back." />
          ) : (
            <ul className="divide-line divide-y">
              {data.issues.map((issue, index) => (
                <li key={index} className="flex items-start gap-3 py-2.5 first:pt-0 last:pb-0">
                  <Badge
                    tone={ISSUE_SEVERITY[issue.severity]?.tone ?? 'neutral'}
                    className="mt-0.5"
                  >
                    {ISSUE_SEVERITY[issue.severity]?.label ?? 'Medium'}
                  </Badge>
                  <div className="min-w-0">
                    <p className="text-ink-muted text-[11px] font-semibold tracking-wide uppercase">
                      {SECTION_LABELS[issue.section] ?? 'General'}
                    </p>
                    <p className="text-ink text-sm">{issue.message}</p>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Improvements
// --------------------------------------------------------------------------- //
function ImprovementsTab({ review }) {
  if (review.isPending) return <Writing />;
  if (review.isError) return <ReviewFailed review={review} />;
  const data = review.data;

  return (
    <Card>
      <SectionTitle
        icon="sparkles"
        title="How to improve it"
        description="Specific changes, most useful first. Only add what is true."
        action={<SourceBadge source={data.source} />}
      />
      {data.suggestions.length === 0 ? (
        <EmptyState compact title="Nothing to change — tailor it to each role you apply for." />
      ) : (
        <ol className="space-y-3">
          {data.suggestions.map((item, index) => (
            <li
              key={index}
              id={`improvement-${index}`}
              className="border-line rounded-input flex scroll-mt-28 gap-3 border p-3.5"
            >
              <span className="bg-brand-600 grid size-7 shrink-0 place-items-center rounded-full text-xs font-semibold text-white">
                {index + 1}
              </span>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <p className="text-ink-muted text-[11px] font-semibold tracking-wide uppercase">
                    {SECTION_LABELS[item.section] ?? 'General'}
                  </p>
                  <CopyButton text={item.example || item.suggestion} />
                </div>
                <p className="text-ink mt-0.5 text-sm">{item.suggestion}</p>
                {item.example && (
                  <p className="bg-brand-50/70 text-ink ring-brand-100 rounded-input mt-2 px-3 py-2 text-sm ring-1 ring-inset">
                    <span className="text-brand-700 mr-1.5 text-xs font-semibold">Try:</span>
                    {item.example}
                  </p>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </Card>
  );
}

function CopyButton({ text }) {
  const toast = useToast();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy — select the text and copy it instead.');
    }
  }

  return (
    <Button size="xs" variant="ghostBrand" onClick={() => void copy()}>
      <Icon name={copied ? 'check' : 'clipboard'} size={13} />
      {copied ? 'Copied' : 'Copy'}
    </Button>
  );
}

// --------------------------------------------------------------------------- //
// Checklist
// --------------------------------------------------------------------------- //
function ChecklistTab({ checks, fixes, gapItems }) {
  const groups = groupChecks(checks);
  const fixFor = Object.fromEntries((fixes ?? []).map((item) => [item.label, item.fix]));
  const total = checks.reduce((sum, check) => sum + check.weight, 0);
  const earned = checks.reduce((sum, check) => sum + (check.passed ? check.weight : 0), 0);

  return (
    <Card>
      <SectionTitle
        icon="check"
        title="Checklist"
        description={`Counted from your CV — ${earned} of ${total} points. Each check is worth the points beside it.`}
      />
      <div className="grid items-start gap-4 lg:grid-cols-2">
        {groups.map((group) => (
          <section key={group.key} className="border-line rounded-input border">
            <header className="border-line bg-surface-muted/40 flex items-center justify-between gap-3 border-b px-3 py-2">
              <h3 className="text-ink flex items-center gap-1.5 text-sm font-semibold">
                <Icon name={group.icon} size={14} className="text-ink-muted" />
                {group.label}
              </h3>
              <span className="text-ink-muted text-xs tabular-nums">
                {group.earned} / {group.possible} pts
              </span>
            </header>
            <ul className="divide-line divide-y">
              {group.items.map((check) => (
                <li
                  key={check.key}
                  id={`check-${check.key}`}
                  className="flex scroll-mt-28 gap-3 px-3 py-2.5"
                >
                  <span
                    aria-hidden="true"
                    className={cn(
                      'mt-0.5 grid size-5 shrink-0 place-items-center rounded-full',
                      check.passed
                        ? 'bg-success-100 text-success-700'
                        : 'bg-danger-100 text-danger-700',
                    )}
                  >
                    <Icon name={check.passed ? 'check' : 'close'} size={12} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <p className="text-ink text-sm font-medium">
                      {check.label}
                      <span className="sr-only">{check.passed ? ' — passed' : ' — missing'}</span>
                    </p>
                    <p className="text-ink-muted text-xs">{check.detail}</p>
                    {!check.passed && fixFor[check.label] && (
                      <p className="text-ink mt-1 text-[13px]">
                        <span className="text-brand-700 font-semibold">How to fix: </span>
                        {fixFor[check.label]}
                      </p>
                    )}
                  </div>
                  <span className="text-ink-muted shrink-0 text-[11px] tabular-nums">
                    {check.weight} pts
                  </span>
                </li>
              ))}
            </ul>
          </section>
        ))}
      </div>
      {gapItems && gapItems.sections.items.length > 0 && (
        <p className="text-ink-muted mt-4 text-xs">
          The gap analysis checks a few more things (summary, projects, dates and more) — see the
          Gap analysis tab.
        </p>
      )}
    </Card>
  );
}

// --------------------------------------------------------------------------- //
// Match to a role
// --------------------------------------------------------------------------- //
function MatchTab({ source }) {
  return (
    <div className="grid items-start gap-4 lg:grid-cols-2">
      <Card>
        <SectionTitle
          icon="search"
          title="Check it against a role"
          description="Each listing shows how this CV covers the skills it asks for — required first, then nice-to-have."
        />
        <ol className="text-ink mb-4 space-y-1.5 text-sm">
          <li className="flex gap-2">
            <span className="text-brand-700 font-semibold">1.</span> Open an internship you like.
          </li>
          <li className="flex gap-2">
            <span className="text-brand-700 font-semibold">2.</span> Look for “How your CV matches”
            on the listing.
          </li>
          <li className="flex gap-2">
            <span className="text-brand-700 font-semibold">3.</span> Add the missing skills you
            really have, then apply.
          </li>
        </ol>
        <LinkButton to={paths.internships} variant="primary">
          <Icon name="search" size={16} />
          Find internships
        </LinkButton>
      </Card>
      <Card>
        <SectionTitle
          icon="route"
          title="Check it against your goal"
          description="Career guidance compares this CV with the field you are aiming for and writes suggestions."
        />
        <LinkButton to={`${paths.student.career}#cv-check`} variant="tintBrand">
          <Icon name="route" size={16} />
          Go to Career guidance
        </LinkButton>
        <p className="text-ink-muted mt-4 text-xs">
          {source === 'ai'
            ? 'Written feedback comes from AI, using your CV’s text only. Your email, phone number and links are removed before it is sent.'
            : 'When AI feedback is available, only your CV’s text is sent — with your email, phone number and links removed.'}
        </p>
      </Card>
    </div>
  );
}
